import { useState } from "react";

type CommunityType = "hoa_large" | "condo" | "hopa" | "hoa_small";
type RecordsStatus = "yes" | "no" | "unsure";
type AdaStatus = "yes" | "no" | "notice";

export default function Assessment() {
  const [step, setStep] = useState<number>(1);
  const [communityType, setCommunityType] = useState<CommunityType | null>(null);
  const [recordsStatus, setRecordsStatus] = useState<RecordsStatus | null>(null);
  const [adaStatus, setAdaStatus] = useState<AdaStatus | null>(null);

  const resetAssessment = () => {
    setStep(1);
    setCommunityType(null);
    setRecordsStatus(null);
    setAdaStatus(null);
  };

  const getRiskLevel = () => {
    if (adaStatus === "notice" || recordsStatus === "no") {
      return {
        level: "High Exposure Risk",
        color: "bg-red-500/10 text-red-700 border-red-200",
        badge: "Priority Remediation Recommended",
        desc: "Your association may be exposed to Florida civil fines or ADA Title III website demand letters. Prompt statutory audit and record vaulting is strongly advised.",
      };
    }
    if (recordsStatus === "unsure" || adaStatus === "no") {
      return {
        level: "Moderate Compliance Gap",
        color: "bg-amber-500/10 text-amber-800 border-amber-200",
        badge: "Statute Audit Advised",
        desc: "Your website likely lacks mandatory official record vaulting or WCAG 2.2 PDF remediation required by Florida law.",
      };
    }
    return {
      level: "Good Standing Checkup",
      color: "bg-primary-500/10 text-primary-800 border-primary-200",
      badge: "Routine Maintenance",
      desc: "Your community has baseline measures in place. A annual compliance review helps maintain your paper trail as statutes evolve.",
    };
  };

  const risk = getRiskLevel();

  return (
    <section id="assessment" className="py-24 md:py-32 bg-background-100 border-y border-background-200">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary-700 font-semibold">
            <i className="ri-shield-user-line" />
            Interactive Tool
          </span>
          <h2 className="mt-4 font-heading text-4xl md:text-5xl font-semibold text-foreground-950 leading-[1.05]">
            Check your community's statutory and WCAG risk level.
          </h2>
          <p className="mt-4 text-foreground-700 text-base md:text-lg leading-relaxed">
            Answer 3 quick questions to evaluate your website's compliance posture under current Florida statutes and ADA guidelines.
          </p>
        </div>

        <div className="max-w-2xl mx-auto bg-background-50 rounded-xl border border-background-200 p-6 sm:p-10 shadow-sm">
          {/* Progress bar */}
          {step <= 3 && (
            <div className="mb-8">
              <div className="flex items-center justify-between text-xs font-semibold text-foreground-600 mb-2">
                <span>STEP {step} OF 3</span>
                <span>{Math.round(((step - 1) / 3) * 100)}% COMPLETE</span>
              </div>
              <div className="h-2 w-full bg-background-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-600 transition-all duration-500"
                  style={{ width: `${Math.round(((step - 1) / 3) * 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Question 1 */}
          {step === 1 && (
            <div>
              <h3 className="font-heading text-xl sm:text-2xl font-semibold text-foreground-950 mb-2">
                1. What is your community structure?
              </h3>
              <p className="text-sm text-foreground-600 mb-6">
                Florida statutes enforce distinct requirements depending on parcel count and housing type.
              </p>
              <div className="space-y-3">
                {[
                  { id: "hoa_large", label: "HOA with 100+ parcels", desc: "Mandated under FS 720.303(5)" },
                  { id: "condo", label: "Condominium Association", desc: "Mandated under FS 718.111(12)" },
                  { id: "hopa", label: "55+ / HOPA Housing", desc: "Requires HUD 80/20 survey compliance" },
                  { id: "hoa_small", label: "HOA under 100 parcels", desc: "Statutory & ADA best practice" },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setCommunityType(item.id as CommunityType);
                      setStep(2);
                    }}
                    className={`w-full text-left p-4 rounded-lg border transition-all flex items-center justify-between group cursor-pointer ${
                      communityType === item.id
                        ? "border-primary-600 bg-primary-50 text-foreground-950"
                        : "border-background-200 bg-background-50 hover:border-primary-300 hover:bg-background-100"
                    }`}
                  >
                    <div>
                      <div className="font-heading font-semibold text-base text-foreground-950">
                        {item.label}
                      </div>
                      <div className="text-xs text-foreground-500 mt-0.5">{item.desc}</div>
                    </div>
                    <i className="ri-arrow-right-s-line text-xl text-foreground-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Question 2 */}
          {step === 2 && (
            <div>
              <h3 className="font-heading text-xl sm:text-2xl font-semibold text-foreground-950 mb-2">
                2. Are official records stored in a secure owner vault?
              </h3>
              <p className="text-sm text-foreground-600 mb-6">
                Meeting notices, financial budgets, contracts, and governing documents must be restricted to verified owners.
              </p>
              <div className="space-y-3">
                {[
                  { id: "yes", label: "Yes, fully restricted & updated", desc: "Password-protected portal active" },
                  { id: "no", label: "No, posted publicly or missing", desc: "Triggers statutory non-compliance" },
                  { id: "unsure", label: "Unsure / Managed by volunteer", desc: "Needs record vaulting audit" },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setRecordsStatus(item.id as RecordsStatus);
                      setStep(3);
                    }}
                    className={`w-full text-left p-4 rounded-lg border transition-all flex items-center justify-between group cursor-pointer ${
                      recordsStatus === item.id
                        ? "border-primary-600 bg-primary-50 text-foreground-950"
                        : "border-background-200 bg-background-50 hover:border-primary-300 hover:bg-background-100"
                    }`}
                  >
                    <div>
                      <div className="font-heading font-semibold text-base text-foreground-950">
                        {item.label}
                      </div>
                      <div className="text-xs text-foreground-500 mt-0.5">{item.desc}</div>
                    </div>
                    <i className="ri-arrow-right-s-line text-xl text-foreground-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all" />
                  </button>
                ))}
              </div>
              <button
                onClick={() => setStep(1)}
                className="mt-6 text-xs text-foreground-500 hover:text-foreground-800 flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-arrow-left-line" /> Back to step 1
              </button>
            </div>
          )}

          {/* Question 3 */}
          {step === 3 && (
            <div>
              <h3 className="font-heading text-xl sm:text-2xl font-semibold text-foreground-950 mb-2">
                3. Has your site undergone an ADA / WCAG audit?
              </h3>
              <p className="text-sm text-foreground-600 mb-6">
                South Florida leads the US in website accessibility demand letters targeting un-remediated PDFs and public pages.
              </p>
              <div className="space-y-3">
                {[
                  { id: "yes", label: "Yes, audited & WCAG 2.2 compliant", desc: "Signed conformance report on file" },
                  { id: "no", label: "No or audited more than 2 years ago", desc: "Elevated ADA litigation risk" },
                  { id: "notice", label: "Received a demand letter / attorney notice", desc: "Urgent 72-hr remediation required" },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setAdaStatus(item.id as AdaStatus);
                      setStep(4);
                    }}
                    className={`w-full text-left p-4 rounded-lg border transition-all flex items-center justify-between group cursor-pointer ${
                      adaStatus === item.id
                        ? "border-primary-600 bg-primary-50 text-foreground-950"
                        : "border-background-200 bg-background-50 hover:border-primary-300 hover:bg-background-100"
                    }`}
                  >
                    <div>
                      <div className="font-heading font-semibold text-base text-foreground-950">
                        {item.label}
                      </div>
                      <div className="text-xs text-foreground-500 mt-0.5">{item.desc}</div>
                    </div>
                    <i className="ri-arrow-right-s-line text-xl text-foreground-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all" />
                  </button>
                ))}
              </div>
              <button
                onClick={() => setStep(2)}
                className="mt-6 text-xs text-foreground-500 hover:text-foreground-800 flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-arrow-left-line" /> Back to step 2
              </button>
            </div>
          )}

          {/* Results Summary */}
          {step === 4 && (
            <div className="text-center py-2">
              <span className={`inline-block px-4 py-1.5 rounded-full text-xs font-semibold border ${risk.color} mb-4`}>
                {risk.badge}
              </span>
              <h3 className="font-heading text-3xl font-semibold text-foreground-950">
                {risk.level}
              </h3>
              <p className="mt-3 text-foreground-700 text-sm leading-relaxed max-w-lg mx-auto">
                {risk.desc}
              </p>

              <div className="mt-8 pt-6 border-t border-background-200 flex flex-col sm:flex-row gap-4 items-center justify-center">
                <a
                  href="#contact"
                  className="w-full sm:w-auto bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold px-7 py-3.5 rounded-md flex items-center justify-center gap-2 cursor-pointer transition-colors text-sm"
                >
                  Book your free compliance consultation
                  <i className="ri-arrow-right-line" />
                </a>
                <button
                  onClick={resetAssessment}
                  className="w-full sm:w-auto border border-background-300 hover:bg-background-100 text-foreground-800 font-medium px-5 py-3.5 rounded-md text-sm cursor-pointer transition-colors"
                >
                  Retake check
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
